import { useState, useRef } from 'react';
import { convertKmlToShp } from '../../utils/kmlToShp';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function KmzToShpTool() {
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef();

  function pick(list) {
    const arr = Array.from(list || []);
    const valid = arr.filter((f) => /\.(kmz|kml)$/i.test(f.name));
    const bad = arr.length - valid.length;
    setError(bad > 0 ? `${bad} file(s) skipped — only .kmz / .kml are supported.` : '');
    setResults([]);
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name));
      return [...prev, ...valid.filter((f) => !names.has(f.name))];
    });
  }

  function removeFile(i) {
    setFiles((prev) => prev.filter((_, x) => x !== i));
  }

  async function convert() {
    if (!files.length) return;
    setWorking(true);
    setError('');
    setResults([]);
    const out = [];
    for (const f of files) {
      try {
        const r = await convertKmlToShp(f);
        out.push({ source: f.name, ...r });
        downloadBlob(r.blob, r.filename);
        await sleep(400); // stagger so the browser doesn't block back-to-back downloads
      } catch (err) {
        out.push({ source: f.name, error: err.message || 'Conversion failed.' });
      }
    }
    setResults(out);
    setWorking(false);
  }

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-gray-400 text-sm">KMZ / KML → Shapefile · Web Mercator · ready for ArcGIS Online</p>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files); }}
        onClick={() => inputRef.current.click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition ${dragging ? 'border-gold-500' : 'border-navy-500 hover:border-gold-500'}`}
        style={{ backgroundColor: '#0d1b2e', borderColor: dragging ? '#c9a84c' : '#1e3a5f' }}
      >
        <p className="text-gray-300 text-sm mb-1">Drag &amp; drop KMZ / KML files here</p>
        <p className="text-gray-500 text-xs">or click to browse — you can pick several at once</p>
        <input
          ref={inputRef}
          type="file"
          accept=".kmz,.kml"
          multiple
          className="hidden"
          onChange={(e) => { pick(e.target.files); e.target.value = ''; }}
        />
      </div>

      {/* Selected files */}
      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between rounded px-3 py-2" style={{ backgroundColor: '#0d1b2e', border: '1px solid #1e3a5f' }}>
              <span className="text-white text-sm truncate">{f.name}</span>
              <button onClick={() => removeFile(i)} className="text-gray-500 hover:text-red-400 transition text-sm shrink-0 ml-3">&times;</button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <button
        onClick={convert}
        disabled={!files.length || working}
        className="w-full py-3 rounded font-bold uppercase tracking-wide text-sm transition disabled:opacity-40"
        style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
      >
        {working ? 'Converting…' : `Convert ${files.length || ''} File${files.length === 1 ? '' : 's'} to Shapefile`}
      </button>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-2">
          {results.map((r, i) => (
            <div key={i} className="rounded-lg border p-3" style={{ backgroundColor: '#0d1b2e', borderColor: r.error ? 'rgba(248,113,113,0.4)' : '#1e3a5f' }}>
              {r.error ? (
                <p className="text-red-400 text-sm"><span className="font-mono text-xs text-gray-400">{r.source}</span> — {r.error}</p>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-green-400 text-sm font-bold truncate">✓ {r.filename}</p>
                    <div className="flex gap-3 text-xs text-gray-400 mt-0.5">
                      {r.summary.POINT > 0 && <span>{r.summary.POINT} pt</span>}
                      {r.summary.POLYLINE > 0 && <span>{r.summary.POLYLINE} line</span>}
                      {r.summary.POLYGON > 0 && <span>{r.summary.POLYGON} poly</span>}
                      {r.multi && <span className="text-gray-500">multi-type</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => downloadBlob(r.blob, r.filename)}
                    className="text-xs px-3 py-1.5 rounded border transition text-gray-300 hover:text-gold-400 shrink-0"
                    style={{ borderColor: '#1e3a5f' }}
                  >
                    Download
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-gray-600 text-xs leading-relaxed">
        Each file downloads as its own Web Mercator (meters) shapefile ZIP. In ArcGIS Online, use
        <span className="text-gray-400"> Content → New item → Your device</span> and drop a ZIP to publish a hosted feature layer, then apply your standard symbology.
        If a browser blocks the automatic downloads, use the Download buttons above.
      </p>
    </div>
  );
}
